import styled from '@emotion/styled';
import { ColumnSelector } from './columnSelector/ColumnSelector';
import { Plus } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { useTableContext } from 'features/misc/table/providers/TableProvider';
import { Filter } from './filter/Filter';
import { Search } from './Search';
import { Selected } from './Selected';
import { Button, ButtonProps } from 'components';
import { Task } from 'features/taskboard/types/Tasks';
import type { SelectedOptions } from '../types';
import { Dropdown } from './DropDown';
import { useUser } from 'features/auth';
import { Box, Divider, useMediaQuery } from '@mui/material';
import { SelectedRowCount } from './SelectedRowCount';
import { ActionsPopup } from './ActionsPopup';
import { useState } from 'react';
import { SettingsButton } from './SettingsButton';
import { useFeatureFlag } from 'features/accessControl';

export type HeaderProps = {
  addButtonName?: string;
  rowSelectionCount?: number;
  filterTitle?: React.ReactNode;
  tableId?: string;
  addButtonProps?: ButtonProps;
  filters?: import('types/table').FilterParams;
};

export const Header = ({
  addButtonName,
  filterTitle,
  rowSelectionCount,
  tableId,
  addButtonProps,
  filters,
}: HeaderProps) => {
  const { table, globalFilter, config } = useTableContext();
  const { data: userData } = useUser();
  const [searchOpen, setSearchOpen] = useState(Boolean(table.options.meta?.tableState?.search));
  const isBetween1250And1520 = useMediaQuery('(min-width:1250px) and (max-width:1520px)');
  const isLessThan1250 = useMediaQuery('(max-width:1250px)');
  const isExportEnabled = useFeatureFlag('ExportDataSupplierManagement');

  const isSearchOpen =
    searchOpen ||
    Boolean(
      table.options.meta?.onTableStateChange
        ? table.options.meta.tableState?.search ?? ''
        : globalFilter ?? ''
    );

  const loggedInUser = userData?.loginContext;
  const handleHeaderOptionClick = (selectedOption: SelectedOptions) => {
    const selectedRows = table.getSelectedRowModel()?.flatRows;
    if (selectedRows) {
      const tasks = selectedRows.map(row => {
        const taskRow = row.original as Task;
        return taskRow;
      });
      if (selectedOption === 'read' || selectedOption === 'unread') {
        table.options.meta?.onHeaderClick(
          [],
          selectedOption,
          selectedRows.map(row => {
            return row.index;
          })
        );
      } else {
        table.options.meta?.onHeaderClick(tasks, selectedOption);
      }
      table.resetRowSelection();
    }
  };
  const getHeaderOptions = (): SelectedOptions[] => {
    const selectedRows = table.getSelectedRowModel()?.flatRows;
    //let selectedOptions
    if (selectedRows) {
      const tasks = selectedRows.map(row => {
        const taskRow = row.original as Task;
        return taskRow;
      });
      const haveUnreadTask = tasks.filter(
        t => t.viewedOn == null && t.assignedToPerson?.id === loggedInUser?.id
      );
      const haveReadTask = tasks.filter(t => t.viewedOn != null);
      if (haveReadTask?.length === 0 && haveUnreadTask?.length === 0) {
        return table.options.meta?.selectedOptions.filter(
          o => o !== 'unread' && o !== 'read'
        ) as SelectedOptions[];
      }
      if (
        (haveReadTask?.length > 0 && haveUnreadTask?.length > 0) ||
        (haveReadTask.length === 0 && haveUnreadTask?.length > 0)
      ) {
        return table.options.meta?.selectedOptions.filter(o => o !== 'unread') as SelectedOptions[];
      }
      if (haveReadTask?.length > 0 && haveUnreadTask?.length === 0) {
        return table.options.meta?.selectedOptions.filter(o => o !== 'read') as SelectedOptions[];
      }
      return table.options.meta?.selectedOptions as SelectedOptions[];
    }
    return table.options.meta?.selectedOptions as SelectedOptions[];
  };

  return (
    <Container>
      <Left>
        <SelectedRowCount rowSelectionCount={rowSelectionCount} />
        <StyledDivider orientation="vertical" />
        <ColumnSelector />
        <Filter title={filterTitle} />
        {isExportEnabled && config?.settingsActions && (
          <SettingsButton
            settingsActions={config?.settingsActions}
            tableId={tableId}
            tableState={table.options.meta?.tableState}
            columns={table.getAllColumns().map(col => ({ id: col.id }))}
            filters={filters}
          />
        )}
        {((isBetween1250And1520 && isSearchOpen) || isLessThan1250) &&
          table.options?.meta?.selectedOptions?.length &&
          Boolean(table.getSelectedRowModel()?.flatRows?.length) && (
            <ActionsPopup>
              <Middle
                sx={{
                  flexDirection: 'column',
                  height: '100%',
                  alignItems: 'flex-start',
                  button: {
                    width: '100%',
                    justifyContent: 'flex-start',
                  },
                }}
              >
                {table.getSelectedRowModel()?.flatRows?.length ? (
                  <Selected options={getHeaderOptions()} onClick={handleHeaderOptionClick} />
                ) : null}
              </Middle>
            </ActionsPopup>
          )}
      </Left>
      {((!isSearchOpen && isBetween1250And1520) || (!isBetween1250And1520 && !isLessThan1250)) && (
        <Middle>
          {table.options?.meta?.selectedOptions?.length &&
          table.getSelectedRowModel()?.flatRows?.length ? (
            <Selected options={getHeaderOptions()} onClick={handleHeaderOptionClick} />
          ) : null}
        </Middle>
      )}
      <Right>
        {addButtonName && typeof table.options?.meta?.addRow === 'string' ? (
          <Link to={table.options.meta.addRow}>
            {addButtonProps ? (
              <Button {...addButtonProps} />
            ) : (
              <Button variant="primary">
                <Plus size={16} color="white" />
                {addButtonName}
              </Button>
            )}
          </Link>
        ) : null}
        {addButtonName && typeof table.options?.meta?.addRow !== 'string' ? (
          <Button
            variant="primary"
            size="lg"
            startIcon={<Plus size={16} color="#FFF" />}
            onClick={() => {
              table.options?.meta?.addRow('topright');
            }}
          >
            {addButtonName}
          </Button>
        ) : null}
        <Dropdown />
      </Right>
      {config?.hideSearch ? null : <Search searchOpen={searchOpen} setSearchOpen={setSearchOpen} />}
    </Container>
  );
};
const Container = styled.section`
  display: flex;
  padding-bottom: 16px;
  padding-top: 16px;
  padding-inline: 0;
  width: 100%;
  align-items: center;
`;

export const List = styled.ul`
  padding: 0;
  margin: 0;
  display: flex;
  list-style: none;
  gap: 0.5rem;
`;

export const Left = styled.div`
  display: flex;
  align-items: center;
  height: 32px;
  gap: 8px;
`;

const Middle = styled(Box)`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  flex: 1;
`;

export const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  margin-right: 8px;
  margin-left: auto;
  & input[type='checkbox'] {
    display: initial;
  }
`;

const StyledDivider = styled(Divider)`
  border-width: 1px;
  border-color: ${p => p.theme.palette.border.primarySubtle};
  height: 24px;
`;
